import { getUnhungPriority, LOADING_PRIORITIES } from 'domains/viewer/loadingPriorities';
import { ExtWindowName } from 'hooks/useExtensionState';
import { getWindowName } from 'hooks/useOpenTabs';
import analytics from 'modules/analytics';
import { CUSTOM_TIMINGS } from 'modules/analytics/constants';
import { UserAnalytics } from 'modules/analytics/analytics';
import { CacheStrategy } from 'modules/viewer/PixelCacheStategy';
import type { PixelDataSharedWorker } from 'modules/viewer/workers/PixelDataSharedWorker';
import {
  analyticsStackInitialLoadsTracker,
  analyticsStackUnhangedLoadsTracker,
  providerMap,
  providerTracker,
} from 'modules/viewer/workers/PixelDataSharedWorker';
import {
  CancelTransferScope,
  FrameData,
  type HandleFrameMessage,
  type HandleInitialFrameMessage,
} from 'modules/viewer/workers/pixelDataSharedWorkerTypes';
import type { FullSingleLayerStack, Stack } from '../../../ViewportsConfigurations/types';
import type { BaseImagingProvider } from './BaseImagingProvider';
import { ImagingProviderLoadType } from './BaseImagingProvider';
import type { ProgressiveImagingProviderArgs } from './ProgressiveImagingProvider';
import { ProgressiveImagingProvider } from './ProgressiveImagingProvider';

export interface WorkerInitializationOptions {
  cacheStrategy: CacheStrategy;
  userAnalytics: Nullable<UserAnalytics>;
  datadogSessionId: Nullable<string>;
  sharedWorkerViewId: Nullable<string>;
}

export type WorkerLoadedImagingProviderArgs<T> = ProgressiveImagingProviderArgs<T> & {
  pixelDataSharedWorker: PixelDataSharedWorker;
  workerInitializationOptions: WorkerInitializationOptions;
};

export class WorkerLoadedImagingProvider<
  T extends FullSingleLayerStack = FullSingleLayerStack,
> extends ProgressiveImagingProvider {
  cacheStrategy: CacheStrategy;
  // VX-4406: set true when the worker's `load-complete` message is routed to this provider, and reset
  // at the start of each (re-)load. It is intentionally decoupled from the per-frame completion that
  // drives `status` (ProgressiveImagingProvider.handleFrame flips status to 'complete' only when
  // _framesLoadedToCache reaches stack.frames.length). The viewer-load ledger reads both so a STRESS
  // viewer-load-timeout can be classified: load-complete received but frames short => the worker
  // over-reported completion / a frame was never delivered (recovery cannot fix); not received +
  // short => delivery was severed.
  loadCompleteReceived: boolean = false;
  // VX-4406: wall-clock ms of the most recent frame delivery, so the ledger can report how long
  // delivery has been idle at a timeout (a large idle = a stalled/backpressured pipeline; a small
  // idle = still actively delivering / just started).
  #lastFrameDeliveredAt: number | null = null;
  // VX-4834: latched when a load-stack/load-frame is posted. See hasRequestedFrames().
  #hasRequestedFrames: boolean = false;
  #loadPromiseResolve: null | (() => void);
  #sharedWorker: PixelDataSharedWorker;
  #pendingLoads: Set<string>; // make private after done debugging
  #windowName: ExtWindowName | null;
  // VX-4775: the delivery mode this provider has most recently communicated to the worker, or null
  // when the worker has not been told anything yet (construction, and after reinitializeWorker binds
  // a fresh port whose connection carries none of the old request state). syncPixelDelivery() posts
  // only on a change, so the memory manager can call it on every config pass for free.
  #cacheOnlyDeliverySentToWorker: boolean | null = null;
  // VX-4775 phase 2: this stack was initially unhanged and VIEWER_RESTRICT_INITIAL_LOADS elected to
  // prefetch it to the on-disk cache only, rather than speculatively filling renderer memory with a
  // series the radiologist may never open. Unlike the loadType-driven half of shouldLoadCacheOnly(),
  // this does not track the memory config — it is set once by ViewerContext when the unhanged load is
  // issued and cleared for good by promoteToDelivery() when the stack enters the layout.
  #restrictedToCacheUntilHanged: boolean = false;
  // VX-5244: the priority ViewerContext wants this stack loaded at right now -- hanged while a viewport shows it,
  // otherwise its upcoming-step or unhung tier. Null until VIEWER_STEP_AWARE_LOAD_PRIORITY assigns one, which keeps the
  // re-drives below at their pre-VX-5244 priorities. Assigned like `loadType`, from the outside, so the re-drives need
  // not work out whether the stack is on screen. Never reset: `useSplitFlag` reads the treatment once per Split client,
  // so the flag cannot turn off under a live provider; the viewer remounts with fresh providers on the next case.
  loadPriority: Nullable<number> = null;
  // VX-5244: the latest update-priority for a load whose `load-stack` has not been posted yet. #loadWithPixelTransfer
  // awaits navigator.storage.persist() before posting, and the worker drops an update for a stack it holds no request
  // for, so an update in that gap (a step change right after the load starts) would otherwise be lost for good. Held
  // only once loadPriority is assigned, so with VIEWER_STEP_AWARE_LOAD_PRIORITY off updates post exactly as before.
  #priorityBeforeLoadStack: Nullable<{ focus: number; priority: number }> = null;
  #isLoadStackPending: boolean = false;
  // Retained as a single bundle so the init message can be re-sent to a fresh port
  // after a watchdog-driven reconnect (VX-4223). The constructor receives these once;
  // a recovered connection needs them again to reconfigure its worker entry. Kept as
  // one object so new init options don't require threading another private field.
  #workerInitializationOptions: WorkerInitializationOptions;

  constructor({
    pixelDataSharedWorker,
    workerInitializationOptions,
    ...progressiveImagingProviderArgs
  }: WorkerLoadedImagingProviderArgs<T>) {
    super(progressiveImagingProviderArgs);

    // legacy, probably safe to remove or upgrade to 'http'
    this.type = 'url-pixels';
    this.providerType = 'worker-loaded';
    this.cacheStrategy = workerInitializationOptions.cacheStrategy;
    this.#loadPromiseResolve = null;
    this.#sharedWorker = pixelDataSharedWorker;
    this.#pendingLoads = new Set();
    this.#windowName = getWindowName(window.location.href);
    this.#workerInitializationOptions = workerInitializationOptions;
    this.#sendInit();

    providerMap.set(this.stack.smid, this);
  }

  /**
   * Sends the `init` message that configures this provider's entry on the shared
   * worker (cache strategy, supported textures, analytics identity). Called once
   * at construction and again by reinitializeWorker() after a reconnect.
   */
  #sendInit(): void {
    this.#sharedWorker.port.postMessage({
      type: 'init',
      SUPPORTED_TEXTURES: this.supportedTextures,
      windowName: this.#windowName,
      ...this.#workerInitializationOptions,
    });
  }

  /**
   * Rebinds this provider to a freshly created shared worker port after the
   * VX-4156 watchdog severs and recreates the connection. Always re-posts `init`
   * so the new worker entry is configured, then recovers whatever transfer was in
   * flight against the dead port so the page self-heals without a manual reload.
   * Completed stacks keep their already-rendered pixels and are not reloaded
   * (VX-4223).
   */
  reinitializeWorker(pixelDataSharedWorker: PixelDataSharedWorker): void {
    this.#sharedWorker = pixelDataSharedWorker;
    this.#sendInit();
    // VX-4775: the fresh connection has no request state, so nothing has been told about this stack's
    // delivery mode yet. Clearing this is what makes the re-driven load below (or the next
    // syncPixelDelivery) re-establish it rather than assume the dead port's mode carried over — a
    // stack left in cache-only mode on a connection that never heard about it would render blank.
    this.#cacheOnlyDeliverySentToWorker = null;

    // A genuine in-flight full-stack load (Full mode) went to the now-dead port and will never complete. Settle the
    // orphaned load promise first so existing awaiters (e.g. ViewerContext's loadUnhangedStacksNext ->
    // waitForLoadFinished) unblock rather than hang forever, then reset and re-drive the transfer on the new port at the
    // stack's original priority (re-issuing everything at HANGED would invert the priority of background/prior stacks
    // against the truly-hanged ones). We re-drive whether the stack is still 'loading' or has been knocked into 'error':
    // a forced generation bump retires the worker mid-load, and a transient error during that churn can flip a
    // still-fully-resident stack out of 'loading'. Without re-driving it as a whole load-stack here it would fall to the
    // per-frame path below — scattering individual load-frame requests at unhung priority — and the new-generation
    // worker would be left idle with the study permanently half-loaded (VX-4393). The loadType guard keeps a stack that
    // left Full mode mid-load (became Initial) out of this whole-stack reload; those recover through the targeted
    // #pendingLoads path below.
    //
    // VX-4775: a cache-only stack re-drives as a whole load-stack too, so the re-sent request carries
    // `cacheOnly` and stays cache-only on the new port. The #pendingLoads path below would instead
    // scatter a `load-frame` per pending frame, and `load-frame` always delivers — quietly
    // resurrecting exactly the transfers cache-only loading exists to prevent.
    if (
      (this.status === 'loading' || this.status === 'error') &&
      (this.loadType === ImagingProviderLoadType.Full || this.shouldLoadCacheOnly())
    ) {
      this._resolveLoad();
      this.#pendingLoads.clear();
      this.setStatus('init');
      this.deferredLoad = null;
      this.load({
        initialSlice: Math.max(this.getActiveSlice('TWO_D_DRE'), 0),
        priority:
          this.loadPriority ??
          (this.wasPartOfInitialLoad() ? LOADING_PRIORITIES.HANGED : getUnhungPriority(this.stack)),
      });
      return;
    }

    // A completed stack pared down to individual frames (e.g. ViewerMemoryManager's
    // Initial load type) may have single-frame loads in flight via loadFrameIntoMemory.
    // #pendingLoads is shared by stack and frame loads, so re-issue only those frames
    // against the new port rather than reloading the whole stack — reloading would undo
    // the memory manager's eviction and contradict the completed-stack invariant.
    if (this.#pendingLoads.size > 0) {
      const pendingFrameSmids = [...this.#pendingLoads];
      this.#pendingLoads.clear();
      pendingFrameSmids.forEach((frameSmid) => {
        const frameIndex = this.stack.frames.findIndex((frame) => frame.smid === frameSmid);
        if (frameIndex >= 0) this.loadFrameIntoMemory(frameIndex);
      });
    }
  }

  cancelLoad() {
    this.#sharedWorker.port.postMessage({
      type: 'cancel-transfer',
      scope: CancelTransferScope.Stack,
      stack: this.stack,
    });
  }

  dispose(): void {
    this.cancelLoad();
    this.#pendingLoads.clear();
    providerTracker.delete(this.stack.smid);
    analyticsStackInitialLoadsTracker.delete(this.stack.smid);
    analyticsStackUnhangedLoadsTracker.delete(this.stack.smid);
    if (providerMap.get(this.stack.smid) === this) {
      providerMap.delete(this.stack.smid);
    }
    super.dispose();
  }

  /**
   * VX-4406: record that the worker reported this stack's load as complete (called by the
   * PixelDataSharedWorker message router on `load-complete`). Distinct from `status === 'complete'`,
   * which is gated on the main thread receiving every frame (see the field comment above) — the
   * viewer-load ledger reads both to classify a viewer-load-timeout.
   */
  markLoadComplete(): void {
    this.loadCompleteReceived = true;
  }

  /** VX-4406: ms since the most recent frame delivery, or null if none delivered yet. */
  msSinceLastFrameDelivered(): number | null {
    return this.#lastFrameDeliveredAt == null ? null : Date.now() - this.#lastFrameDeliveredAt;
  }

  /**
   * VX-4834: true once this provider has actually posted a pixel request (`load-stack` or
   * `load-frame`) to the SharedWorker.
   *
   * The diagnostic this exists for: STRESS runs produce failing cases with 8-20 series but ZERO
   * SharedWorker requests and zero bytes, on a single port the worker reports healthy — and cases
   * with the SAME port state load fine. So the port is not the discriminator, and from outside there
   * was no way to separate "the viewer asked and got nothing" (delivery) from "the viewer never
   * asked" (load gating). `isLoading() && !hasRequestedFrames()` is precisely the second case.
   *
   * Deliberately a plain latch rather than a count: the question is whether the ask ever happened,
   * and a count would invite reading request volume as progress when frames may still never arrive.
   */
  hasRequestedFrames(): boolean {
    return this.#hasRequestedFrames;
  }

  _loadStack(initialSlice: number, priority: number): Promise<void> {
    // VX-4406: a fresh load attempt has not yet been acked by the worker, nor delivered any frames
    // yet; reset both so the ledger reflects this attempt rather than a prior one (e.g. after a
    // watchdog-driven re-drive) — otherwise msSinceLastFrameDelivered() would report a stale idle
    // time inherited from the previous attempt and misclassify an active re-drive as stalled.
    this.loadCompleteReceived = false;
    this.#lastFrameDeliveredAt = null;
    this.stack.frames.forEach((frame) => {
      this.#pendingLoads.add(frame.smid);
    });

    return this.#loadWithPixelTransfer(initialSlice, priority);
  }

  handleFrame(frameData: HandleFrameMessage | HandleInitialFrameMessage | FrameData): void {
    // VX-4462: the base drops a post-dispose frame, but the withheld-frame re-request below would still run and post a
    // fresh `load-frame` for a stack dispose() already cancelled (dispose also cleared #pendingLoads and the loaded
    // set, so nothing else stops it).
    if (this.isDisposed) return;
    super.handleFrame(frameData);
    this.#pendingLoads.delete(frameData.frameSmid);
    this.#lastFrameDeliveredAt = Date.now(); // VX-4406: delivery-idle clock for the ledger

    // VX-4775: a frame can be withheld and then promoted — the worker decides delivery when the
    // frame's loader callback fires, so a frame already in flight at that moment arrives pixel-less
    // even though this provider now wants it resident. The memory manager's promotion pass can't
    // recover it (loadFrameIntoMemory skips frames still in #pendingLoads, which this one was), so
    // re-request it here, after the delete above has cleared that guard. Reads from the cache the
    // withheld frame was written to.
    //
    // This guard is load-bearing and NOT redundant with _shouldLoadFrameIntoMemory: a restricted stack
    // stays at loadType Full while being withheld, so every one of its frames would otherwise look
    // like a gap and get re-requested as a `load-frame` — which always delivers. That would defeat the
    // restriction outright and cost more than never having applied it, since the stack would be
    // fetched cache-only and then re-fetched frame by frame.
    //
    // Deliberately narrower than shouldLoadCacheOnly(): a restricted stack at `Initial` still wants
    // its active slice resident (see ViewerMemoryManager), so a withheld frame there IS a real gap.
    // `None` needs no mention — _shouldLoadFrameIntoMemory already returns false for it.
    const isWithholdingEveryFrame =
      this.#restrictedToCacheUntilHanged && this.loadType === ImagingProviderLoadType.Full;
    if (frameData.pixels == null && !isWithholdingEveryFrame) {
      const frameIndex = this.getFrameIndex(frameData.frameSmid);
      if (frameIndex != null && this._shouldLoadFrameIntoMemory(frameIndex)) {
        this.loadFrameIntoMemory(frameIndex);
      }
    }
  }

  updateLoadPriority({ focus, priority }: { focus: number; priority: number }) {
    if (this.#isLoadStackPending && this.loadPriority != null) {
      this.#priorityBeforeLoadStack = { focus, priority };
      return;
    }
    this.#sharedWorker.port.postMessage({
      type: 'update-priority',
      stackSmid: this.stack.smid,
      focus,
      isDropped: this.isDroppedStack(),
      priority,
    });
  }

  _resolveLoad() {
    this.#loadPromiseResolve?.();
  }

  #loadWithPixelTransfer(initialSlice: number, priority: number): Promise<void> {
    this.#isLoadStackPending = true;
    this.#priorityBeforeLoadStack = null;
    return new Promise(async (resolve: (result: Promise<void> | void) => void) => {
      this.#loadPromiseResolve = resolve;
      analytics.timing(CUSTOM_TIMINGS.viewer.workerImagingProvider.loadStack);
      providerTracker.set(this.stack.smid, this);

      if (this.wasPartOfInitialLoad()) {
        analyticsStackInitialLoadsTracker.add(this.stack.smid);
      } else {
        analyticsStackUnhangedLoadsTracker.add(this.stack.smid);
      }

      // Request persistent storage for site as we will be storing the pixel data using IndexedDB
      if (navigator?.storage?.persist) {
        try {
          const isPersisted = await navigator.storage.persist();

          if (isPersisted === false) {
            console.warn('Persistent storage was not granted');
          }
        } catch (err: any) {
          console.warn('Persistent storage request failed', err);
        }
      }

      // VX-4775: the request itself establishes this stack's delivery mode on the worker, so record
      // what we sent — syncPixelDelivery() diffs against it rather than re-posting on every pass.
      const cacheOnly = this.shouldLoadCacheOnly();
      this.#cacheOnlyDeliverySentToWorker = cacheOnly;
      this.#hasRequestedFrames = true;
      const latestUpdate = this.#priorityBeforeLoadStack;
      this.#isLoadStackPending = false;
      this.#priorityBeforeLoadStack = null;
      this.#sharedWorker.port.postMessage({
        type: 'load-stack',
        // Once we've downgraded to per-frame (e.g. a failed volume allocation), is3Dable() is the
        // source of truth — propagate it so the worker doesn't re-attempt the volume allocation on
        // reconnect/reload (it reads request.stack.is3Dable directly to gate volume allocation).
        stack: this.is3Dable() ? this.stack : { ...this.stack, is3Dable: false },
        initialSlice: latestUpdate?.focus ?? initialSlice,
        partOfInitialLoad: this.wasPartOfInitialLoad(),
        isDropped: this.isDroppedStack(),
        priority: latestUpdate?.priority ?? priority,
        cacheOnly,
        windowName: this.#windowName,
      });
    });
  }

  /**
   * VX-4775: true when this stack's frames should be loaded into the cache but not transferred into
   * the renderer. Two independent reasons:
   *
   * - `ImagingProviderLoadType.None` — ProgressiveImagingProvider.handleFrame would discard every
   *   frame on arrival, so delivering them is pure allocation and GC churn (phase 1).
   * - the stack was initially unhanged and VIEWER_RESTRICT_INITIAL_LOADS restricted it (phase 2).
   */
  shouldLoadCacheOnly(): boolean {
    return this.loadType === ImagingProviderLoadType.None || this.#restrictedToCacheUntilHanged;
  }

  /**
   * VX-4775 phase 2: mark this stack as prefetch-to-cache-only. Must be called before `load()` so the
   * `load-stack` message carries the mode; a stack already mid-delivery is not retroactively withheld.
   */
  restrictDeliveryUntilHanged(): void {
    this.#restrictedToCacheUntilHanged = true;
  }

  /** VX-4775 phase 2: whether this stack is still waiting on its first drop into a viewport. */
  isRestrictedUntilHanged(): boolean {
    return this.#restrictedToCacheUntilHanged;
  }

  /**
   * VX-4775 phase 2: the stack has entered the layout, so lift the restriction and get its pixels into
   * renderer memory. `reloadAllFramesIntoMemory` covers both shapes this can take: a load still in
   * flight re-requests just the frames that already arrived without pixels (the rest now deliver
   * because syncPixelDelivery flipped the worker's mode), while a finished cache-only load re-drives
   * the whole stack — reading back from the cache the prefetch just filled, which is the latency this
   * trade buys with the memory saving.
   *
   * Idempotent: the restriction is cleared first, so a second call (e.g. a re-render of the layout
   * effect) short-circuits rather than re-driving the load.
   */
  promoteToDelivery(): void {
    if (!this.#restrictedToCacheUntilHanged) return;

    this.#restrictedToCacheUntilHanged = false;
    // Only re-point the worker's live request when a load is genuinely still in flight. When nothing
    // is pending, reloadAllFramesIntoMemory()'s whole-stack branch below re-drives a fresh load-stack
    // that already carries the correct cacheOnly value, and by then the worker has already deleted
    // this stack's prior request on load-complete — calling syncPixelDelivery() first would only post
    // a guaranteed no-op update-delivery-mode message.
    if (this.#pendingLoads.size > 0) this.syncPixelDelivery();
    this.reloadAllFramesIntoMemory();
  }

  /**
   * VX-4775: bring the worker's delivery mode for this stack in line with the current load type. Sent
   * as its own message rather than folded into `update-priority` so a stack promoted out of
   * `None` mid-load keeps its transfer, its queue position and its already-written cache entries —
   * only the remaining frames start arriving with pixels attached.
   */
  syncPixelDelivery(): void {
    // Null means no `load-stack` has established a request for this stack on the current connection,
    // so there is nothing on the worker to re-point; whenever that request is eventually sent it
    // carries the mode itself.
    if (this.#cacheOnlyDeliverySentToWorker === null) return;

    const cacheOnly = this.shouldLoadCacheOnly();
    if (cacheOnly === this.#cacheOnlyDeliverySentToWorker) return;

    this.#cacheOnlyDeliverySentToWorker = cacheOnly;
    this.#sharedWorker.port.postMessage({
      type: 'update-delivery-mode',
      stackSmid: this.stack.smid,
      cacheOnly,
    });
  }

  loadFrameIntoMemory(
    frameIndex: number,
    options?: {
      unloadOthers: boolean;
    }
  ) {
    const unloadOthers = options?.unloadOthers ?? false;

    if (unloadOthers) {
      this.stack.frames.forEach((frame, idx) => {
        if (idx !== frameIndex) {
          this.unloadFrameFromMemory(idx);
        }
      });
    }
    const frame = this.stack.frames[frameIndex];
    if (
      frame != null &&
      !this._frameLoadingStateMap[this.stack.smid]?.has(frame.smid) &&
      !this.#pendingLoads.has(frame.smid)
    ) {
      this.#pendingLoads.add(frame.smid);
      this.#hasRequestedFrames = true;
      this.#sharedWorker.port.postMessage({
        type: 'load-frame',
        // Keep the worker's view of is3Dable consistent with the provider after a downgrade.
        stack: this.is3Dable() ? this.stack : { ...this.stack, is3Dable: false },
        frameIndex,
        // VX-5244: an on-screen stack promoted mid-load re-requests frames here; keep it off the unhung tier.
        priority: this.loadPriority ?? getUnhungPriority(this.stack),
        windowName: this.#windowName,
      });
    }
  }

  reloadAllFramesIntoMemory(): void {
    if (this.#pendingLoads.size === 0) {
      // VX-4775: a stack that already finished a cache-only pass (or was previously fully loaded and
      // later unloaded for memory pressure) has _framesLoadedToCache already at stack.frames.length,
      // even though nothing is actually resident. Without clearing it, the very first frame of this
      // reload would immediately re-satisfy handleFrame()'s completion check and flip status back to
      // 'complete' (readyToRender() true) after just one real frame, rendering a mostly-blank volume
      // as if fully loaded.
      this._framesLoadedToCache.clear();
      this.setStatus('init');
      this.deferredLoad = null;
      this.load({
        initialSlice: Math.max(this.getActiveSlice('TWO_D_DRE'), 0),
        // VX-5244: the memory manager reloads any stack that becomes Full, on screen or not, so a hard-coded HANGED let
        // an off-screen stack compete with the step being read.
        priority: this.loadPriority ?? LOADING_PRIORITIES.HANGED,
      });
    } else {
      this.stack.frames.forEach((frame, frameIndex) => {
        // a load is ongoing, but early frames may have been cache-only
        // reload those frames directly to fill in early gaps
        if (!this.#pendingLoads.has(frame.smid)) {
          this.loadFrameIntoMemory(frameIndex);
        }
      });
    }
  }

  forceReloadStack(): Promise<void> {
    this.unloadAllFrames();
    this.#pendingLoads.clear();
    this.reloadAllFramesIntoMemory();
    return this.deferredLoad ?? Promise.resolve();
  }
}

/**
 * Type guard keyed on `providerType` rather than `instanceof`, so callers outside this module (e.g.
 * the viewer-load ledger) can narrow to `WorkerLoadedImagingProvider` without depending on the class
 * for anything but its type.
 */
export const isWorkerLoadedImagingProvider = (
  provider: BaseImagingProvider<Stack>
): provider is WorkerLoadedImagingProvider => provider.providerType === 'worker-loaded';
